"use client";

import { useLocale, useTranslations } from "next-intl";
import { CommonModal } from "@/components/common/CommonModal";

type ExamplesModalProps = {
    open: boolean;
    onClose: () => void;
    onSelect: (text: string) => void;
};

export function ExamplesModal({ open, onClose, onSelect }: ExamplesModalProps) {
    const locale = useLocale();
    const isKo = locale === "ko";
    const t = useTranslations("home");

    const handleSelect = (text: string) => {
        onSelect(text);
        onClose();
    };

    const closingLine = isKo
        ? "마음에 드는 문장을 골라 오늘의 한 줄에 바로 넣어보세요."
        : "Pick a line you like and we’ll put it into your card.";

    const section1 = isKo
        ? {
              title: "하루를 마무리하는 한 줄",
              items: [
                  "오늘은 여기까지. 그래도 잘했다.",
                  "조금 느려도, 오늘도 한 칸은 나아갔다.",
                  "버텨낸 하루도, 분명히 나를 앞으로 데려간다.",
                  "지쳤지만, 이 하루가 내 이야기를 조금 더 채워 주었다.",
              ],
          }
        : {
              title: "Lines to end your day",
              items: [
                  "That’s enough for today. I still did well.",
                  "Slow, but I still moved one step forward today.",
                  "Even a day of just holding on still takes me forward.",
                  "I’m tired, but this day still added a little more to my story.",
              ],
          };

    const section2 = isKo
        ? {
              title: "집중과 몰입을 기록하는 한 줄",
              items: [
                  "오늘의 집중은 내일의 나를 조금 더 가볍게 만든다.",
                  "완벽하지 않아도, 끝까지 읽고, 쓰고, 버텼다.",
                  "한 줄이라도 쓰고 자는 내가, 예전의 나보다 좋다.",
                  "오늘의 집중은 남에게 보이지 않아도, 내 안에 쌓인다.",
              ],
          }
        : {
              title: "Lines about focus and effort",
              items: [
                  "Today’s focus makes tomorrow’s me a little lighter.",
                  "Not perfect, but I kept reading, writing, and staying with it.",
                  "Going to bed after writing even one line feels better than before.",
                  "Today’s focus may be invisible to others, but it’s stacking up inside me.",
              ],
          };

    const section3 = isKo
        ? {
              title: "쉬어 가는 날을 위한 한 줄",
              items: [
                  "아무것도 안 한 하루 같아도, 몸과 마음은 분명히 쉬고 있었다.",
                  "오늘의 목표는 ‘버티기’ 대신 ‘쉬기’였다.",
                  "천천히 마신 커피 한 잔이, 오늘의 하이라이트.",
                  "아무 일도 일어나지 않은 하루도, 충분히 괜찮은 하루다.",
              ],
          }
        : {
              title: "Lines for slow and resting days",
              items: [
                  "It felt like I did nothing, but my body and mind clearly rested.",
                  "Today’s goal was not to endure, but to rest.",
                  "A slowly enjoyed cup of coffee was today’s highlight.",
                  "A day where nothing special happened is still a perfectly fine day.",
              ],
          };

    const section4 = isKo
        ? {
              title: "관계와 대화를 남기는 한 줄",
              items: [
                  "오래된 이야기보다, 오늘 나눈 짧은 안부가 더 고마웠다.",
                  "괜찮냐는 한마디가, 오늘을 끝까지 버티게 했다.",
                  "같은 이야기도, 그 사람이 해서 더 힘이 났다.",
                  "말이 다 하지 못한 마음은, 이렇게 한 줄로라도 남겨 둔다.",
              ],
          }
        : {
              title: "Lines about people and conversations",
              items: [
                  "Today’s short check-in meant more than long stories from the past.",
                  "One “Are you okay?” helped me make it through the day.",
                  "The same words felt different because they came from that person.",
                  "Feelings that words couldn’t carry, I’m leaving here as a single line.",
              ],
          };

    const sections = [section1, section2, section3, section4];

    return (
        <CommonModal open={open} onClose={onClose} ariaLabelledBy="examples-modal-title">
            <div
                className="max-h-[80vh] w-[70vw] max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 text-slate-900 shadow-xl sm:p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between gap-2">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400 sm:text-[13px]">
                            {isKo ? "오늘의 한 줄" : "Today’s one-line"}
                        </p>
                        <h2
                            id="examples-modal-title"
                            className="text-base font-semibold text-slate-900 sm:text-lg"
                        >
                            {isKo ? "예시 문장 모음" : "Example lines"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="focus-ring inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label={t("shareModalCancel")}
                    >
                        ✕
                    </button>
                </header>

                <p className="mt-3 text-[13px] text-slate-500 sm:text-sm">{closingLine}</p>

                <div className="mt-5 space-y-5">
                    {sections.map((section) => (
                        <section
                            key={section.title}
                            className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5"
                        >
                            <h3 className="text-sm font-semibold text-slate-900 sm:text-base">{section.title}</h3>
                            <div className="mt-3 space-y-[10px]">
                                {section.items.map((line) => (
                                    <button
                                        key={line}
                                        type="button"
                                        onClick={() => handleSelect(line)}
                                        className="w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[13px] text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 sm:text-sm"
                                    >
                                        {line}
                                    </button>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </CommonModal>
    );
}

